import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/browser';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';

import { getProduct } from '../../pages/api';

import { Skeleton, SkeletonWrapper } from '../../atoms/skeleton';
import Button from '../../atoms/button';
import Carousel from '../../atoms/carousel';
import ProductDetails from '../../organs/pdp-details';

const QuickShopWrapper = styled.section(({ theme, position }) => ({
  borderBottom: `1px solid ${theme.color.brand}`,
  borderTop: `1px solid ${theme.color.brand}`,
  display: 'none',
  margin: '2rem 0',
  minHeight: '30rem',
  overflow: 'hidden',
  padding: '4rem 0',
  position: 'relative',
  gridColumnEnd: -1,
  gridColumnStart: 1,
  gridRowStart: Math.ceil(position / 3) + 1,
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    display: 'flex'
  }
}));

const QuickCarousel = styled(Carousel)(() => ({
  textAlign: 'center',
  width: '40%',
  img: { maxWidth: '100%' }
}));

const QuickClose = styled(Button)(({ theme }) => {
  const crossStyles = css`
    ${theme.close(theme.color.brand, 'center center', 20)}
    border: 0;
    height: 3.2rem;
    outline: 0;
    width: 3.2rem;
    position: absolute;
    right: 0;
    top: 4rem;
  `;

  return crossStyles;
});

const QuickShop = ({
  slug,
  position,
  onQuickShopClose,
  ...props
}) => {
  const ref = useRef();

  const [product, setProduct] = useState(null);

  const getProductResponse = async () => {
    await getProduct({ id: slug }).then((res) => {
      if (res.errors) {
        Sentry.captureException(res);
        toast(res.errors[0].message, { type: toast.TYPE.ERROR });
        onQuickShopClose();
        return;
      }

      setProduct(res);
    }).catch((error) => {
      onQuickShopClose();
      Sentry.captureException(error);
    });
  };

  useEffect(() => {
    getProductResponse();

    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, []);

  if (!product) {
    return (
      <QuickShopWrapper ref={ref} position={position} {...props}>
        <SkeletonWrapper css={{ alignItems: 'center' }}>
          <Skeleton
            css={{
              maxHeight: 300,
              maxWidth: 300,
              height: '100%',
              width: '100%',
              marginBottom: 10
            }}
          />
          <Skeleton css={{ height: 18, width: 50 }} />
        </SkeletonWrapper>

        <SkeletonWrapper css={{ alignItems: 'center' }}>
          <Skeleton css={{ height: 36, width: '75%', marginBottom: 10 }} />
          <Skeleton css={{ height: 36, width: '90%', marginBottom: 10 }} />
          <Skeleton css={{ height: 18, width: 100, marginBottom: 30 }} />
          <Skeleton
            css={{
              height: 50,
              width: '50%',
              alignSelf: 'start',
              marginBottom: 10
            }}
          />
          <Skeleton css={{ height: 40, marginBottom: 10 }} />
          <Skeleton css={{ height: 40, marginBottom: 10 }} />
        </SkeletonWrapper>
      </QuickShopWrapper>
    );
  }

  return (
    <QuickShopWrapper ref={ref} position={position} {...props}>
      { product.title }
      <QuickCarousel
        type="quickshop"
        options={{
          prevNextButtons: false,
          pageDots: true
        }}
      >
        {
          product.master.images.map(((image) => (
            <div key={`quickshop-image-${image.id}`}>
              <img
                key={image.id}
                alt={product.master.name}
                src={image.product_url}
              />
            </div>
          )))
        }
      </QuickCarousel>

      <ProductDetails
        product={product}
        position={position}
        insights={props.insights}
        isQuickshop
      />

      <QuickClose aria-label="close quick shop" onClick={onQuickShopClose} isIcon outline />
    </QuickShopWrapper>
  );
};

QuickShop.propTypes = {
  onQuickShopClose: PropTypes.func.isRequired,
  position: PropTypes.number.isRequired,
  slug: PropTypes.string.isRequired,
  insights: PropTypes.func.isRequired
};

export default QuickShop;
